
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Cpu, Database, Server, Globe, Code, Layers, Shield, Terminal, Zap, Fingerprint, Activity } from 'lucide-react';

const TechCard = ({ title, tech, desc, icon: Icon }: { title: string; tech: string; desc: string; icon: any }) => (
    <div className="p-6 bg-white dark:bg-[#15171C] border border-stone-100 dark:border-[#2D3142] rounded-2xl hover:border-nobel-gold/50 transition-all duration-300 shadow-sm hover:shadow-xl group">
        <div className="flex items-center gap-4 mb-5">
            <div className="w-10 h-10 bg-stone-50 dark:bg-white/5 rounded-xl flex items-center justify-center text-stone-900 dark:text-white shadow-inner group-hover:bg-nobel-gold group-hover:text-black transition-colors">
                <Icon size={20} />
            </div>
            <div>
                <h4 className="font-bold text-stone-900 dark:text-white text-sm tracking-tight">{title}</h4>
                <div className="text-[10px] font-mono text-nobel-gold uppercase font-bold">{tech}</div>
            </div>
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">{desc}</p>
    </div>
);

export const Technologies = () => (
  <div className="space-y-16 animate-fade-in">
    <section className="max-w-3xl">
        <h3 className="text-3xl font-serif font-bold text-stone-900 dark:text-white mb-6">The High-Performance Stack</h3>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-light">
            GhostPay is engineered using best-in-class technologies. We prioritize <span className="text-stone-900 dark:text-white font-bold">type-safety, memory safety, and parallel execution</span> to deliver a reliable commerce experience for global merchants.
        </p>
    </section>

    <div className="space-y-12">
        {/* Core Protocols */}
        <section>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-6 border-l-2 border-nobel-gold pl-4">Blockchain Protocols</div>
            <div className="grid md:grid-cols-2 gap-6">
                <TechCard 
                    title="Solana Mainnet"
                    tech="Rust & Anchor 0.30"
                    icon={Terminal}
                    desc="Leveraging Anchor for strictly validated Program Derived Addresses (PDAs) and sub-400ms deterministic finality."
                />
                <TechCard 
                    title="Sui Network"
                    tech="Move & Object Model"
                    icon={Layers}
                    desc="Utilizing Move's object-centric model for parallel transaction processing and capability-based access control."
                />
            </div>
        </section>

        {/* Runtime & Logic */}
        <section>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-6 border-l-2 border-nobel-gold pl-4">Runtime & Execution</div>
            <div className="grid md:grid-cols-3 gap-6">
                <TechCard 
                    title="GhostOS Runtime"
                    tech="Node.js 22 & TypeScript"
                    icon={Server}
                    desc="The orchestrator handling on-chain event correlation, x402 micropayment routing, and real-time state synchronization."
                />
                <TechCard 
                    title="Database Layer"
                    tech="Neon / PostgreSQL"
                    icon={Database}
                    desc="Serverless relational storage for off-chain order indexing, merchant configurations, and high-frequency data."
                />
                <TechCard 
                    title="Zero-Knowledge"
                    tech="Circom / SnarkJS"
                    icon={Shield}
                    desc="Cryptographic proofs ensuring transaction validity without exposing merchant-customer relationship metadata."
                />
            </div>
        </section>

        {/* Interface & Mobile */}
        <section>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-6 border-l-2 border-nobel-gold pl-4">Client-Side Architecture</div>
            <div className="grid md:grid-cols-3 gap-6">
                <TechCard 
                    title="Web Interface"
                    tech="React 19 & Tailwind"
                    icon={Globe}
                    desc="Highly responsive UI leveraging concurrent rendering for zero-latency interactions across all devices."
                />
                <TechCard 
                    title="Mobile Core"
                    tech="React Native / Expo"
                    icon={Fingerprint}
                    desc="Native mobile application utilizing the device's Secure Enclave for biometric transaction authorization."
                />
                <TechCard 
                    title="Price Oracles"
                    tech="Pyth Network SDK"
                    icon={Activity}
                    desc="Sub-second price feeds ensuring accurate conversion rates for multi-asset checkout flows."
                />
            </div>
        </section>
    </div>

    {/* Performance Summary Footer */}
    <section className="bg-stone-50 dark:bg-white/[0.02] p-8 rounded-[32px] border border-stone-200 dark:border-white/10 flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex items-center gap-12 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-700">
             <div className="flex flex-col">
                <span className="text-2xl font-serif font-bold text-stone-900 dark:text-white">1.0.0 Beta</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Current Phase</span>
             </div>
             <div className="flex flex-col">
                <span className="text-2xl font-serif font-bold text-stone-900 dark:text-white">400ms</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Avg Settlement</span>
             </div>
             <div className="flex flex-col">
                <span className="text-2xl font-serif font-bold text-stone-900 dark:text-white">0.1%</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Standard Fee</span>
             </div>
        </div>
        <div className="flex gap-4">
             <span className="px-3 py-1 bg-white dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-lg text-[10px] font-mono font-bold text-stone-400">BUILD: MAINNET_READY</span>
        </div>
    </section>
  </div>
);
